import React, { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import BoxedTitle from "@/components/BoxedTitle";
import { clearCache } from "@/pages/admin/adminCache";

const links = [
  { to: "/", label: "Home", testid: "nav-home-link" },
  { to: "/about", label: "About", testid: "nav-about-link" },
  { to: "/portfolio", label: "Portfolio", testid: "nav-portfolio-link" },
  { to: "/services", label: "Services", testid: "nav-services-link" },
  { to: "/history", label: "History", testid: "nav-history-link" },
  { to: "/contact", label: "Contact", testid: "nav-contact-link" },
];

const KEY_STORAGE = "vbb_admin_key";

/**
 * Reads sessionStorage on every render. useLocation() subscribes the
 * component to route changes (login redirect, sign-out nav), so reading
 * sessionStorage during render — instead of via useEffect — avoids the
 * one-tick lag where the navbar shows the stale state.
 *
 * Returns [signedIn, forceRefresh] where forceRefresh handles the rare
 * case where sessionStorage changes without a route change.
 */
function useAdminSession() {
  useLocation(); // subscribe to route changes
  const [, setTick] = useState(0);
  const signedIn = !!sessionStorage.getItem(KEY_STORAGE);
  const refresh = () => setTick((n) => n + 1);
  return [signedIn, refresh];
}

export default function Navbar() {
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();
  const [signedIn, refreshSession] = useAdminSession();

  const handleSignOut = () => {
    sessionStorage.removeItem(KEY_STORAGE);
    clearCache();
    setOpen(false);
    refreshSession();
    navigate("/");
  };

  return (
    <header
      className="sticky top-0 z-40 bg-[#fafafa]/90 backdrop-blur vbb-border-b"
      data-testid="site-navbar"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="flex items-center justify-between h-[72px]">
          <Link
            to="/"
            className="flex items-center gap-1"
            data-testid="nav-logo-link"
          >
            <span className="font-display text-[15px] tracking-tight uppercase pl-[0.35em]">
              <BoxedTitle size="sm">V</BoxedTitle>
              ery Big Box
            </span>
            <span className="font-mono-tiny text-[#6b6b6b] hidden sm:inline ml-2">
              LLC
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `vbb-link font-mono-tiny ${
                    isActive ? "text-[#0a0a0a]" : "text-[#6b6b6b]"
                  } hover:text-[#0a0a0a]`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-5">
            {signedIn ? (
              <button
                onClick={handleSignOut}
                data-testid="nav-signout-btn"
                className="hidden md:inline text-[11px] tracking-[0.18em] uppercase text-[#e34234] hover:text-[#0a0a0a] transition-colors"
              >
                Sign out
              </button>
            ) : (
              <Link
                to="/login"
                data-testid="nav-login-link"
                className="hidden md:inline text-[11px] tracking-[0.18em] uppercase text-[#9a9a9a] hover:text-[#0a0a0a] transition-colors"
              >
                Login
              </Link>
            )}
            <button
              data-testid="nav-mobile-toggle"
              aria-label="Toggle menu"
              className="lg:hidden w-9 h-9 border border-[#0a0a0a] flex items-center justify-center"
              onClick={() => setOpen((v) => !v)}
            >
              <span className="block w-4 h-px bg-[#0a0a0a] relative before:content-[''] before:absolute before:-top-1.5 before:left-0 before:w-4 before:h-px before:bg-[#0a0a0a] after:content-[''] after:absolute after:top-1.5 after:left-0 after:w-4 after:h-px after:bg-[#0a0a0a]" />
            </button>
          </div>
        </div>

        {open && (
          <div
            className="lg:hidden vbb-border-t py-4"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-3">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  onClick={() => setOpen(false)}
                  className="font-mono-tiny py-2"
                  data-testid={`mobile-${l.testid}`}
                >
                  {l.label}
                </NavLink>
              ))}
              {signedIn ? (
                <button
                  onClick={handleSignOut}
                  className="font-mono-tiny py-2 text-[#e34234] text-left"
                  data-testid="mobile-nav-signout-btn"
                >
                  Sign out
                </button>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setOpen(false)}
                  className="font-mono-tiny py-2 text-[#9a9a9a]"
                  data-testid="mobile-nav-login-link"
                >
                  Login
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
